import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import FWOSelectInput from '../../form/FWOSelectInput'
import FWOTextInput from '../../form/FWOTextInput'
import { SelectItem } from '../../../models/SelectIem'
import { FeedbackReferenceTypes, ILookupItem } from '../../../models/Feedback'
import { buildLookupSelectItems } from '../../../utilities/commonFunctions'
import Conditional from '../../common/Conditional'
import { useField } from 'formik'

export interface IAboutCustomerProps {
  customerReferenceTypes: ILookupItem[]
  responseRequiredOptions: ILookupItem[]
}

interface ILookupOptions {
  customerReferenceOptions: SelectItem[]
  responseRequiredOptions: SelectItem[]
}

const AboutCustomer = forwardRef((props: IAboutCustomerProps, ref) => {
  const mountedRef = useRef(false)
  const [options, setOptions] = useState<ILookupOptions>({
    customerReferenceOptions: [],
    responseRequiredOptions: [],
  })
  const [selection, setSelection] = useState({
    referenceType: null,
    responseRequired: false,
    clear: false,
  })

  const [, , accountEmailHelpers] = useField('accountEmail')
  const [, , crnHelpers] = useField('crn')
  const [, , matterNumberHelpers] = useField('matterNumber')
  const [, , enquiryNumberHelpers] = useField('enquiryNumber')
  const [, , lastNameHelpers] = useField('lastName')
  const [, , firstNameHelpers] = useField('firstName')
  const [, , emailAddressHelpers] = useField('emailAddress')
  const [, , phoneNumberHelpers] = useField('phoneNumber')

  useImperativeHandle(ref, () => ({
    clear() {
      accountEmailHelpers.setValue('')
      crnHelpers.setValue('')
      matterNumberHelpers.setValue('')
      enquiryNumberHelpers.setValue('')
      lastNameHelpers.setValue('')
      firstNameHelpers.setValue('')
      emailAddressHelpers.setValue('')
      phoneNumberHelpers.setValue('')
      setSelection((prevState) => {
        return {
          ...prevState,
          referenceType: null,
          responseRequired: false,
          clear: true,
        }
      })
    },
  }))

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      setOptions((prevState) => {
        return {
          ...prevState,
          customerReferenceOptions:
            props.customerReferenceTypes === undefined
              ? []
              : buildLookupSelectItems(props.customerReferenceTypes, true),
          responseRequiredOptions:
            props.responseRequiredOptions === undefined
              ? []
              : buildLookupSelectItems(props.responseRequiredOptions, true),
        }
      })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  const onCustomerReferenceSelection = (value: string) => {
    let valueSelection = value
    if (
      value.toLowerCase() ===
        String(FeedbackReferenceTypes.PreferNotToAnswer).toLowerCase() ||
      value.toLowerCase() === String(FeedbackReferenceTypes.No).toLowerCase()
    ) {
      valueSelection = null
    }
    setSelection((prevState) => ({
      ...prevState,
      referenceType: valueSelection,
    }))

    accountEmailHelpers.setValue('')
    crnHelpers.setValue('')
    matterNumberHelpers.setValue('')
    enquiryNumberHelpers.setValue('')
  }

  const onResponseSelection = (value: string) => {
    let visible = false
    if (
      value !== undefined &&
      value !== null &&
      value.toLowerCase() === 'yes'
    ) {
      visible = true
    }

    setSelection((prevState) => ({
      ...prevState,
      responseRequired: visible,
    }))

    lastNameHelpers.setValue('')
    firstNameHelpers.setValue('')
    emailAddressHelpers.setValue('')
    phoneNumberHelpers.setValue('')
  }

  return (
    <div>
      <p></p>
      <fieldset id="aboutCustomerSection" className="anonymous-fieldset-title">
        <legend> Tell us a little more about yourself </legend>

        <FWOSelectInput
          name="customerReferenceTypeId"
          label="Do you have an account or reference with us? "
          placeholder={'Choose one'}
          required={true}
          options={options.customerReferenceOptions}
          loading={false}
          aria-label={'Customer Reference Type'}
          title={'Customer Reference Type'}
          onSelection={onCustomerReferenceSelection}
          clearSelection={selection.clear}
        />
        <Conditional
          if={
            selection.referenceType !== null &&
            selection.referenceType.toLowerCase() ===
              String(FeedbackReferenceTypes.MyAccount).toLowerCase()
          }
        >
          <FWOTextInput
            type="text"
            placeholder=""
            label="Please provide your My account email "
            ariaLabel="Please provide your My account email"
            name="accountEmail"
            required={true}
            limit={100}
          />
        </Conditional>
        <Conditional
          if={
            selection.referenceType !== null &&
            selection.referenceType.toLowerCase() ===
              String(FeedbackReferenceTypes.CRN).toLowerCase()
          }
        >
          <FWOTextInput
            type="text"
            placeholder=""
            label="Please provide your CRN "
            ariaLabel="Please provide your CRN"
            name="crn"
            required={true}
            limit={50}
          />
        </Conditional>
        <Conditional
          if={
            selection.referenceType !== null &&
            selection.referenceType.toLowerCase() ===
              String(FeedbackReferenceTypes.MatterNumber).toLowerCase()
          }
        >
          <FWOTextInput
            type="text"
            placeholder=""
            label="Please provide your matter number "
            ariaLabel="Please provide your matter number"
            name="matterNumber"
            required={true}
            limit={50}
          />
        </Conditional>
        <Conditional
          if={
            selection.referenceType !== null &&
            selection.referenceType.toLowerCase() ===
              String(FeedbackReferenceTypes.EnquiryNumber).toLowerCase()
          }
        >
          <FWOTextInput
            type="text"
            placeholder=""
            label="Please provide your enquiry number "
            ariaLabel="Please provide your enquiry number"
            name="enquiryNumber"
            required={true}
            limit={50}
          />
        </Conditional>
        <FWOSelectInput
          name="responseRequired"
          label="Do you want a response to your feedback? "
          placeholder={'Choose one'}
          required={true}
          options={options.responseRequiredOptions}
          loading={false}
          onSelection={onResponseSelection}
          clearSelection={selection.clear}
        />
        <Conditional if={selection.responseRequired}>
          <FWOTextInput
            type="text"
            placeholder=""
            label="First name "
            ariaLabel="First name"
            name="firstName"
            required={true}
            limit={100}
          />
          <FWOTextInput
            type="text"
            placeholder=""
            label="Last name "
            ariaLabel="Last name"
            name="lastName"
            required={true}
            limit={100}
          />
        </Conditional>
        <FWOTextInput
          type="text"
          placeholder=""
          label={`Email address${
            selection.responseRequired ? '' : ' (optional)'
          }`}
          ariaLabel="Email address"
          name="emailAddress"
          required={true}
          limit={100}
        />
        <Conditional if={selection.responseRequired}>
          <FWOTextInput
            type="text"
            placeholder=""
            label="Phone number "
            ariaLabel="Phone number"
            name="phoneNumber"
            required={true}
            limit={20}
          />
        </Conditional>
      </fieldset>
    </div>
  )
})

AboutCustomer.displayName = 'AboutCustomer'

export default AboutCustomer
